/**
 * BE-TAYE IA — Page fullscreen (Claude/ChatGPT-like)
 * Sidebar historique à gauche + zone chat centrale + upload de fichiers
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles, Send, Loader2, Plus, MessageSquare,
  Trash2, ArrowRight, Bot, User as UserIcon, Paperclip,
  X, FileText, Image as ImageIcon, ChevronLeft, Zap, Check,
  History, Undo2, AlertCircle
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Bouton d'action : soit navigation simple, soit exécution avec confirmation.
 */
function ActionButton({ action, token, onNavigate }) {
  const [executing, setExecuting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [done, setDone] = useState(null);

  if (action.kind === "navigate" || !action.kind) {
    return (
      <button
        onClick={() => onNavigate(action.route)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-indigo-200 text-indigo-700 text-xs font-medium hover:bg-indigo-50"
        data-testid={`nav-action-btn`}
      >
        {action.label}
        <ArrowRight className="w-3 h-3" />
      </button>
    );
  }

  if (action.kind === "pdf") {
    // Ouvre le PDF dans un nouvel onglet avec le token en query param
    const url = action.url.startsWith("http")
      ? action.url
      : `${API}${action.url}${action.url.includes("?") ? "&" : "?"}token=${token}`;
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-emerald-200 text-emerald-700 text-xs font-medium hover:bg-emerald-50"
        data-testid="pdf-action-btn"
      >
        <FileText className="w-3 h-3" />
        {action.label}
      </a>
    );
  }

  // action.kind === "execute"
  const execute = async () => {
    setExecuting(true);
    try {
      const res = await axios.post(`${API}/api/assistant/execute-action`,
        { action_type: action.action_type, params: action.params },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDone({ ok: true, message: res.data?.message });
      toast.success(res.data?.message || "Action exécutée");
    } catch (e) {
      setDone({ ok: false, message: e.response?.data?.detail || "Échec" });
      toast.error(e.response?.data?.detail || "Échec de l'action");
    } finally {
      setExecuting(false);
    }
  };

  if (done) {
    return (
      <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${
        done.ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : "bg-red-50 text-red-700 border border-red-200"
      }`}>
        {done.ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
        {done.message}
      </div>
    );
  }

  if (!confirmed) {
    return (
      <div className="w-full">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm space-y-2">
          <div className="flex items-start gap-2">
            <Zap className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
            <div>
              <div className="font-medium text-amber-900">Action à exécuter</div>
              <div className="text-amber-800 mt-0.5">{action.description}</div>
              <div className="text-xs text-amber-600 mt-1 font-mono">
                {action.action_type} · {JSON.stringify(action.params)}
              </div>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setDone({ ok: false, message: "Annulé" })}
              className="px-3 py-1.5 rounded-lg text-xs border border-slate-200 hover:bg-slate-100"
              data-testid="action-cancel-btn"
            >
              Annuler
            </button>
            <button
              onClick={() => { setConfirmed(true); execute(); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-medium hover:bg-amber-700"
              data-testid="action-confirm-btn"
            >
              <Check className="w-3 h-3" />
              Confirmer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 text-xs">
      <Loader2 className="w-3 h-3 animate-spin" />
      Exécution…
    </div>
  );
}

const PAGE_LABELS = {
  "/": "Tableau de bord",
  "/engagements": "Dépenses avec Engagements",
  "/engagements/contrats": "Liste des contrats",
  "/rh": "Ressources Humaines",
  "/rh/salaries": "Liste des salariés",
  "/fleet": "Parc Auto",
  "/fleet/vehicles": "Liste des véhicules",
  "/droit-societes": "Droit des Sociétés",
  "/comptabilite": "Comptabilité",
  "/assistant": "Assistant IA",
};

const getPageContext = (pathname) => {
  const sorted = Object.keys(PAGE_LABELS).sort((a, b) => b.length - a.length);
  const match = sorted.find((p) => pathname === p || pathname.startsWith(p + "/"));
  return match ? PAGE_LABELS[match] : `Page ${pathname}`;
};

const SAMPLE_PROMPTS = [
  { icon: "📅", label: "Mes échéances à venir", prompt: "Quels sont les contrats et engagements qui arrivent à échéance ou en préavis dans les 90 prochains jours ?" },
  { icon: "💰", label: "Coût total mensuel", prompt: "Combien je paie de leasing, assurance et prêts par mois au total ?" },
  { icon: "🚗", label: "Coût par véhicule", prompt: "Quel véhicule me coûte le plus cher au kilomètre ?" },
  { icon: "📝", label: "Rédiger une résiliation", prompt: "Aide-moi à rédiger une lettre de résiliation pour un de mes contrats." },
  { icon: "🔍", label: "Retrouver dans Coffre-Fort", prompt: "Retrouve-moi la clause d'indexation dans mon bail commercial." },
  { icon: "👥", label: "Résumé RH", prompt: "Combien de salariés actifs, quel coût mensuel total, et y a-t-il des acomptes en attente ?" },
];

const TOOL_LABELS = {
  search_sales_invoices: "Recherche factures émises…",
  search_supplier_invoices: "Recherche factures fournisseurs…",
  search_contracts: "Recherche contrats…",
  search_vehicles: "Recherche véhicules…",
  search_employees: "Recherche salariés…",
  search_advance_payments: "Recherche acomptes…",
  search_legal_documents: "Recherche Coffre-Fort…",
  get_stats: "Calcul des statistiques…",
};

export default function AssistantPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [conversations, setConversations] = useState([]);
  const [currentConvId, setCurrentConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamedContent, setStreamedContent] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [toolActivity, setToolActivity] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const abortRef = useRef(null);

  const fetchAuditLog = async () => {
    setAuditLoading(true);
    try {
      const res = await axios.get(`${API}/api/assistant/audit-log`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAuditLog(res.data.logs || []);
    } catch {
      toast.error("Impossible de charger l'historique");
    } finally {
      setAuditLoading(false);
    }
  };

  const rollbackAction = async (auditId) => {
    if (!window.confirm("Annuler cette action ? L'état précédent sera restauré.")) return;
    try {
      await axios.post(`${API}/api/assistant/actions/${auditId}/rollback`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Action annulée");
      fetchAuditLog();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rollback impossible");
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamedContent]);

  const fetchConversations = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/assistant/conversations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConversations(res.data.conversations || []);
    } catch {
      // silent
    }
  }, [token]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const loadConversation = async (convId) => {
    try {
      const res = await axios.get(`${API}/api/assistant/conversations/${convId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCurrentConvId(convId);
      setMessages(res.data.messages || []);
      setStreamedContent("");
      setAttachments([]);
    } catch {
      toast.error("Impossible de charger la conversation");
    }
  };

  const newConversation = () => {
    setCurrentConvId(null);
    setMessages([]);
    setStreamedContent("");
    setAttachments([]);
  };

  const deleteConv = async (convId, e) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer cette conversation ?")) return;
    try {
      await axios.delete(`${API}/api/assistant/conversations/${convId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (convId === currentConvId) newConversation();
      fetchConversations();
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const uploaded = [];
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(`${API}/api/assistant/upload-attachment`, fd, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
        uploaded.push(res.data);
      }
      setAttachments((prev) => [...prev, ...uploaded]);
      toast.success(`${uploaded.length} fichier(s) prêt(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'upload");
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const sendMessage = async (customPrompt = null) => {
    const text = (customPrompt ?? input).trim();
    if ((!text && attachments.length === 0) || streaming) return;

    let convId = currentConvId;
    if (!convId) {
      try {
        const res = await axios.post(`${API}/api/assistant/conversations`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        convId = res.data.id;
        setCurrentConvId(convId);
      } catch {
        toast.error("Impossible de créer la conversation");
        return;
      }
    }

    const currentAttachments = [...attachments];
    const userMsg = {
      role: "user",
      content: text || "(Analyse des documents joints)",
      attachments: currentAttachments.map((a) => ({ filename: a.filename, content_type: a.content_type })),
      id: `tmp-${Date.now()}`,
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setAttachments([]);
    setStreaming(true);
    setStreamedContent("");

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await fetch(`${API}/api/assistant/chat/stream`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          conversation_id: convId,
          message: text || "Analyse les documents joints.",
          page_context: getPageContext(location.pathname),
          attachments: currentAttachments.map((a) => ({
            filename: a.filename,
            content_type: a.content_type,
            extracted_text: a.extracted_text,
          })),
        }),
        signal: controller.signal,
      });

      if (!response.ok || !response.body) throw new Error(`HTTP ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let fullContent = "";
      let finalActions = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          try {
            const evt = JSON.parse(line.slice(5).trim());
            if (evt.type === "delta") {
              fullContent += evt.content;
              setStreamedContent(fullContent);
            } else if (evt.type === "tool_use") {
              const label = TOOL_LABELS[evt.name] || evt.name;
              setToolActivity(label);
            } else if (evt.type === "done") {
              finalActions = evt.actions || [];
              setToolActivity(null);
            } else if (evt.type === "error") {
              toast.error(evt.message || "Erreur assistant");
              setToolActivity(null);
            }
          } catch {
            // ignore
          }
        }
      }

      const cleanedContent = fullContent
        .replace(/\[ACTION:[^|\]]+\|[^\]]+\]/g, "")
        .replace(/\[PDF:[^|\]]+\|[^\]]+\]/g, "")
        .replace(/\[EXECUTE:[^|\]]+\|[^|\]]+\|[^\]]+\]/g, "")
        .trim();
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: cleanedContent, actions: finalActions, id: `asst-${Date.now()}` },
      ]);
      setStreamedContent("");
      fetchConversations();
    } catch (e) {
      if (e.name !== "AbortError") toast.error("Erreur lors de la génération");
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const stopStreaming = () => abortRef.current?.abort();

  const renderContent = (content) => {
    if (!content) return null;
    const clean = content
      .replace(/\[ACTION:[^|\]]+\|[^\]]+\]/g, "")
      .replace(/\[PDF:[^|\]]+\|[^\]]+\]/g, "")
      .replace(/\[EXECUTE:[^|\]]+\|[^|\]]+\|[^\]]+\]/g, "");
    return clean.split("\n").map((line, i) => {
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      return (
        <div key={i}>
          {parts.map((p, j) =>
            p.startsWith("**") && p.endsWith("**") ? (
              <strong key={j}>{p.slice(2, -2)}</strong>
            ) : (
              <span key={j}>{p}</span>
            )
          )}
        </div>
      );
    });
  };

  return (
    <div className="flex h-[calc(100vh-3rem)] -m-6 bg-white" data-testid="assistant-page">
      {/* Sidebar historique */}
      <div
        className={`${
          sidebarOpen ? "w-64" : "w-0"
        } transition-all overflow-hidden bg-slate-50 border-r border-slate-200 flex flex-col shrink-0`}
      >
        <div className="p-3 border-b border-slate-200">
          <button
            onClick={newConversation}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
            data-testid="new-conv-btn"
          >
            <Plus className="w-4 h-4" />
            Nouvelle conversation
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {conversations.length === 0 ? (
            <div className="text-center text-slate-400 text-xs py-6">Aucune conversation</div>
          ) : (
            conversations.map((c) => (
              <div
                key={c.id}
                onClick={() => loadConversation(c.id)}
                className={`group flex items-center gap-2 p-2 rounded-lg cursor-pointer text-sm ${
                  c.id === currentConvId ? "bg-indigo-100 text-indigo-900" : "hover:bg-slate-100 text-slate-700"
                }`}
                data-testid={`conv-item-${c.id}`}
              >
                <MessageSquare className="w-3.5 h-3.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="truncate">{c.title}</div>
                </div>
                <button
                  onClick={(e) => deleteConv(c.id, e)}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded"
                >
                  <Trash2 className="w-3 h-3 text-red-500" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main chat */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="px-6 py-3 border-b border-slate-200 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen((v) => !v)}
              className="p-1.5 rounded hover:bg-slate-100"
              title={sidebarOpen ? "Masquer" : "Afficher"}
            >
              <ChevronLeft className={`w-4 h-4 transition-transform ${sidebarOpen ? "" : "rotate-180"}`} />
            </button>
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-semibold text-slate-900">BE-TAYE IA</div>
              <div className="text-xs text-slate-500">Accès total à vos données</div>
            </div>
          </div>
          <button
            onClick={() => { setAuditOpen(true); fetchAuditLog(); }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm text-slate-700"
            data-testid="audit-log-btn"
            title="Historique des actions exécutées par l'IA"
          >
            <History className="w-4 h-4" />
            Historique actions
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="max-w-3xl mx-auto space-y-5">
            {messages.length === 0 && !streaming && (
              <div className="text-center py-12 space-y-6">
                <div className="w-20 h-20 mx-auto rounded-3xl bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                  <Sparkles className="w-10 h-10 text-indigo-600" />
                </div>
                <div>
                  <div className="text-2xl font-semibold text-slate-900">Bonjour, comment puis-je vous aider ?</div>
                  <div className="text-sm text-slate-500 mt-2">
                    Je suis votre assistant intelligent BE-TAYE. J'ai accès à vos contrats, engagements, salariés,
                    véhicules, factures et documents du Coffre-Fort. Vous pouvez aussi me joindre des PDF ou images.
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4">
                  {SAMPLE_PROMPTS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(s.prompt)}
                      className="text-left p-4 rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/30 text-sm transition group"
                      data-testid={`sample-prompt-${i}`}
                    >
                      <span className="text-2xl mr-2">{s.icon}</span>
                      <div className="mt-1 font-medium text-slate-900">{s.label}</div>
                      <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{s.prompt}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m) => (
              <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
                {m.role === "assistant" && (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center shrink-0">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                )}
                <div
                  className={`max-w-[75%] rounded-2xl px-4 py-3 text-sm ${
                    m.role === "user"
                      ? "bg-indigo-600 text-white rounded-tr-sm"
                      : "bg-slate-100 text-slate-900 rounded-tl-sm"
                  }`}
                >
                  {m.attachments && m.attachments.length > 0 && (
                    <div className="mb-2 space-y-1">
                      {m.attachments.map((a, i) => (
                        <div key={i} className="flex items-center gap-1.5 text-xs opacity-90">
                          {a.content_type?.startsWith("image/") ? (
                            <ImageIcon className="w-3.5 h-3.5" />
                          ) : (
                            <FileText className="w-3.5 h-3.5" />
                          )}
                          {a.filename}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="whitespace-pre-wrap break-words">{renderContent(m.content)}</div>
                  {m.actions && m.actions.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {m.actions.map((a, i) => (
                        <ActionButton
                          key={i}
                          action={a}
                          token={token}
                          onNavigate={navigate}
                        />
                      ))}
                    </div>
                  )}
                </div>
                {m.role === "user" && (
                  <div className="w-8 h-8 rounded-full bg-slate-300 flex items-center justify-center shrink-0">
                    <UserIcon className="w-4 h-4 text-slate-700" />
                  </div>
                )}
              </div>
            ))}

            {streaming && streamedContent && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center shrink-0">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="max-w-[75%] rounded-2xl rounded-tl-sm px-4 py-3 text-sm bg-slate-100 text-slate-900">
                  <div className="whitespace-pre-wrap break-words">
                    {renderContent(streamedContent)}
                    <span className="inline-block w-1.5 h-4 bg-indigo-600 ml-0.5 animate-pulse" />
                  </div>
                </div>
              </div>
            )}

            {streaming && !streamedContent && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center shrink-0">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-slate-100 rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
                  {toolActivity && (
                    <span className="text-xs text-slate-600" data-testid="tool-activity">
                      {toolActivity}
                    </span>
                  )}
                </div>
              </div>
            )}

            {streaming && streamedContent && toolActivity && (
              <div className="flex gap-3 pl-11">
                <div className="text-xs text-indigo-600 flex items-center gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  {toolActivity}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input area */}
        <div className="border-t border-slate-200 px-6 py-4 shrink-0">
          <div className="max-w-3xl mx-auto">
            {/* Attachments preview */}
            {attachments.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-2">
                {attachments.map((a, i) => (
                  <div key={i} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 text-sm">
                    {a.content_type?.startsWith("image/") ? (
                      <ImageIcon className="w-3.5 h-3.5 text-indigo-600" />
                    ) : (
                      <FileText className="w-3.5 h-3.5 text-indigo-600" />
                    )}
                    <span className="max-w-[200px] truncate">{a.filename}</span>
                    <button onClick={() => removeAttachment(i)} className="ml-1 hover:bg-slate-200 rounded p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-end gap-2 rounded-2xl border border-slate-300 p-2 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-100 bg-white">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.txt,.csv,.md,.docx"
                onChange={(e) => handleFileUpload(Array.from(e.target.files))}
                className="hidden"
                data-testid="file-input"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || streaming}
                className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 disabled:opacity-40"
                title="Joindre un fichier (PDF, image…)"
                data-testid="attach-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
              </button>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Posez votre question ou joignez un document…"
                rows={1}
                className="flex-1 resize-none border-0 focus:outline-none text-sm py-2 max-h-40 bg-transparent"
                disabled={streaming}
                data-testid="chat-input"
              />
              {streaming ? (
                <button
                  onClick={stopStreaming}
                  className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700"
                  title="Arrêter"
                >
                  <X className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() && attachments.length === 0}
                  className="p-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40"
                  data-testid="send-btn"
                >
                  <Send className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="text-[11px] text-slate-400 mt-2 text-center">
              BE-TAYE IA peut se tromper. Vérifiez les informations importantes.
            </div>
          </div>
        </div>
      </div>

      {/* Audit log modal */}
      {auditOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setAuditOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <History className="w-5 h-5 text-indigo-600" />
                <div>
                  <div className="font-semibold text-slate-900">Historique des actions BE-TAYE IA</div>
                  <div className="text-xs text-slate-500">Toutes les actions exécutées sur votre confirmation</div>
                </div>
              </div>
              <button onClick={() => setAuditOpen(false)} className="p-1.5 rounded hover:bg-slate-100">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {auditLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              ) : auditLog.length === 0 ? (
                <div className="text-center py-8 text-slate-500 text-sm">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  Aucune action exécutée pour l&apos;instant.
                </div>
              ) : (
                <div className="divide-y">
                  {auditLog.map((log) => (
                    <div key={log.id} className="py-3 flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        log.rolled_back ? "bg-slate-100 text-slate-500"
                          : log.status === "success" ? "bg-emerald-100 text-emerald-700"
                          : "bg-red-100 text-red-700"
                      }`}>
                        {log.rolled_back ? <Undo2 className="w-4 h-4" />
                          : log.status === "success" ? <Check className="w-4 h-4" />
                          : <X className="w-4 h-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900">
                          {log.result?.message || log.action_type}
                          {log.rolled_back && <span className="ml-2 text-xs text-slate-500">(annulée)</span>}
                          {log.status === "failed" && <span className="ml-2 text-xs text-red-600">(échec)</span>}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 font-mono">
                          {log.action_type} · {JSON.stringify(log.params).slice(0, 100)}
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5">
                          {new Date(log.created_at).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })}
                          {log.user_email && ` · ${log.user_email}`}
                        </div>
                        {log.error && <div className="text-xs text-red-600 mt-1">⚠ {log.error}</div>}
                      </div>
                      {log.status === "success" && !log.rolled_back && log.result?.rollback_available && (
                        <button
                          onClick={() => rollbackAction(log.id)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs border border-amber-300 text-amber-800 hover:bg-amber-50"
                          data-testid={`rollback-${log.id}`}
                        >
                          <Undo2 className="w-3 h-3" />
                          Annuler
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
